import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop, useReportCardMode } from '../components/crm/reportCards';
import { Users, UserCheck, UserPlus, Calendar, MapPin, TrendingUp, DollarSign, CheckCircle, BarChart3, Activity, Edit3, Save, X, Upload, Trash2, Eye, EyeOff } from 'lucide-react';
import { getSummitEventDetails, saveSummitEventDetails, getSummitRegistrations, getSummitStats, getSummitDashboardVisibility, setSummitDashboardVisibility } from '../services/summitService';
import { useNavigate } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import {
  GLASS_TEXT,
  GLASS_FIELD,
  GLASS_TH,
  GLASS_ROW,
  GLASS_INSET,
  GLASS_SECONDARY_BUTTON,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassCircleButton,
  GlassFigure,
  GlassBadge,
  GlassAccentButton,
  GlassAvatar,
  GlassSearch,
  GlassEmpty,
  SlidingPills,
} from '../components/glass/GlassUi';

const TAB_OPTIONS = [
  { value: 'registrations', label: 'Registrations' },
  { value: 'event details', label: 'Event Details' },
];

const MUTED = 'text-gray-500 dark:text-white/[0.45]';
const VALUE = 'text-gray-600 dark:text-white/70';
const ICON_MUTED = 'text-gray-400 dark:text-white/30';
const LABEL = 'mb-2 block text-sm font-medium text-gray-700 dark:text-white/70';
const MODAL_LABEL = 'mb-1 block text-sm font-medium text-gray-500 dark:text-white/[0.45]';
const DROPZONE = 'flex items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-black/10 bg-black/[0.03] dark:border-white/[0.12] dark:bg-white/[0.04]';
const IMAGE_FRAME = 'rounded-2xl border border-black/[0.06] dark:border-white/[0.08]';
// Red-outlined pill for the edit-mode "Remove" actions
const REMOVE_BUTTON = 'inline-flex flex-shrink-0 items-center justify-center rounded-full border border-[#F80707]/30 px-4 py-2 text-sm font-medium text-[#E00606] transition hover:bg-[#F80707]/[0.08] dark:border-[#F80707]/40 dark:text-[#FF4D40] dark:hover:bg-[#F80707]/[0.12]';
// File-picker <label> styled like GlassAccentButton (a <label> can't be that <button>)
const UPLOAD_LABEL = 'inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#F80707] px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_24px_-10px_rgba(248,7,7,0.75)] transition hover:bg-[#C90505] active:scale-[0.98]';
const REGISTRATION_MODAL_PANEL = GLASS_MODAL_PANEL.replace('max-w-lg', 'max-w-2xl');
const SPONSOR_TIER_TONE = { Platinum: 'lavender', Gold: 'amber', Silver: 'stone', Bronze: 'coral' };

const SummitOverview = () => {
  const navigate = useNavigate();
  // My view renders this page hidden: show both tabs' cards so either can be pinned
  const cardMode = useReportCardMode();
  const [activeTab, setActiveTab] = useState('registrations');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summitStats, setSummitStats] = useState({});
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTarget, setEditingTarget] = useState(null);
  const [tempTargetValue, setTempTargetValue] = useState('');
  const [uploadingImages, setUploadingImages] = useState({});
  const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false);
  const [uploadingActivityImages, setUploadingActivityImages] = useState({});
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showOnDashboard, setShowOnDashboard] = useState(true);
  const [dashboardCardLoading, setDashboardCardLoading] = useState(true);
  const [savingDashboardCard, setSavingDashboardCard] = useState(false);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [stats, regs, eventDetails] = await Promise.all([
          getSummitStats(),
          getSummitRegistrations(),
          getSummitEventDetails()
        ]);
        
        console.log('Loaded registrations:', regs); // Debug log
        
        setSummitStats(stats || {});
        setRegistrations(regs || [
          // Sample data if API doesn't return anything
          {
            id: '1',
            primaryRegistrant: {
              firstName: 'John',
              lastName: 'Doe',
              title: 'CEO',
              email: 'john.doe@example.com'
            },
            company: 'ABC Construction',
            registrationDate: new Date('2024-01-15'),
            guests: [
              { firstName: 'Jane', lastName: 'Doe' }
            ]
          },
          {
            id: '2',
            primaryRegistrant: {
              firstName: 'Sarah',
              lastName: 'Smith',
              title: 'Operations Manager',
              email: 'sarah.smith@example.com'
            },
            company: 'XYZ Builders',
            registrationDate: new Date('2024-01-16'),
            guests: []
          }
        ]);
        setEventDetails(eventDetails || {});
      } catch (error) {
        console.error('Error loading summit data:', error);
        // Set empty defaults if API calls fail
        setSummitStats({});
        setRegistrations([]);
        setEventDetails({});
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Load whether the BBA Summit card is currently on client dashboards
  useEffect(() => {
    const loadVisibility = async () => {
      try {
        setDashboardCardLoading(true);
        setShowOnDashboard(await getSummitDashboardVisibility());
      } catch (error) {
        console.error('Error loading summit dashboard visibility:', error);
      } finally {
        setDashboardCardLoading(false);
      }
    };
    loadVisibility();
  }, []);

  const handleToggleDashboardCard = async () => {
    const nextValue = !showOnDashboard;
    try {
      setSavingDashboardCard(true);
      await setSummitDashboardVisibility(nextValue);
      setShowOnDashboard(nextValue);
    } catch (error) {
      console.error('Error updating summit dashboard visibility:', error);
      alert('Failed to update the dashboard card. Please try again.');
    } finally {
      setSavingDashboardCard(false);
    }
  };



  // Filter registrations by search term
  const filteredRegistrations = registrations.filter(reg => {
    if (!reg) return false;
    const searchLower = searchTerm.toLowerCase();
    return (
      reg.firstName?.toLowerCase().includes(searchLower) ||
      reg.lastName?.toLowerCase().includes(searchLower) ||
      reg.title?.toLowerCase().includes(searchLower) ||
      reg.email?.toLowerCase().includes(searchLower) ||
      reg.userEmail?.toLowerCase().includes(searchLower) ||
      reg.company?.toLowerCase().includes(searchLower) ||
      // Also search in guest names
      reg.guests?.some(guest => 
        guest.firstName?.toLowerCase().includes(searchLower) ||
        guest.lastName?.toLowerCase().includes(searchLower)
      )
    );
  });

  // Event details data
  const [eventDetails, setEventDetails] = useState({
    startDate: '2024-07-15',
    endDate: '2024-07-17',
    location: 'Nashville Convention Center, Nashville, TN',
    overview: {
      whyAttend: 'Join industry leaders and experts for three days of intensive learning, networking, and strategic planning. The BBA Summit is your opportunity to gain exclusive insights, connect with peers, and accelerate your business growth.',
      knowledgeSkills: [
        'Advanced business strategy and scaling techniques',
        'Latest industry trends and market analysis',
        'Financial management and optimization strategies',
        'Team building and leadership development',
        'Technology integration and automation',
        'Customer acquisition and retention strategies'
      ],
      resourcesSupport: [
        'Exclusive access to BBA resource library',
        'One-on-one consulting sessions',
        'Networking events and peer groups',
        'Follow-up support and accountability',
        'Access to expert panel discussions',
        'Comprehensive event materials and recordings'
      ]
    },
    schedule: [
      { day: 'Day 1 - July 15', events: ['Opening Keynote (9:00 AM)', 'Strategy Sessions (10:30 AM)', 'Networking Lunch (12:00 PM)', 'Workshop Sessions (2:00 PM)', 'Evening Reception (6:00 PM)'] },
      { day: 'Day 2 - July 16', events: ['Morning Keynote (8:30 AM)', 'Breakout Sessions (10:00 AM)', 'Panel Discussions (2:00 PM)', 'Interactive Workshops (4:00 PM)', 'Awards Dinner (7:00 PM)'] },
      { day: 'Day 3 - July 17', events: ['Closing Keynote (9:00 AM)', 'Strategy Implementation (10:30 AM)', 'Q&A Sessions (2:00 PM)', 'Closing Ceremony (4:00 PM)'] }
    ],
    speakers: [
      { name: 'Dr. Sarah Johnson', title: 'Business Strategy Expert', company: 'Johnson Consulting Group', bio: 'Leading authority on business scaling and strategic planning with 20+ years of experience.', image: '' },
      { name: 'Michael Chen', title: 'Technology Integration Specialist', company: 'TechBuild Solutions', bio: 'Expert in digital transformation and automation for construction businesses.', image: '' },
      { name: 'Lisa Rodriguez', title: 'Financial Management Advisor', company: 'Rodriguez Financial Group', bio: 'Specialist in financial optimization and growth strategies for small businesses.', image: '' },
      { name: 'David Thompson', title: 'Leadership Development Coach', company: 'Thompson Leadership Institute', bio: 'Dedicated to building high-performing teams and developing leadership skills.', image: '' }
    ],
    sponsors: [
      { name: 'TechBuild Solutions', tier: 'Platinum', logo: 'https://example.com/techbuild-logo.png', description: 'Leading technology solutions provider for construction businesses.' },
      { name: 'Financial Partners Group', tier: 'Gold', logo: 'https://example.com/fpg-logo.png', description: 'Specialized financial services for growing businesses.' },
      { name: 'Construction Supply Co.', tier: 'Silver', logo: 'https://example.com/csc-logo.png', description: 'Premium construction materials and equipment supplier.' }
    ],
    learningTracks: [
      {
        name: 'Business Strategy & Scaling',
        sessions: [
          { title: 'Strategic Planning Fundamentals', duration: '90 min', speaker: 'Dr. Sarah Johnson' },
          { title: 'Scaling Operations', duration: '90 min', speaker: 'Michael Chen' },
          { title: 'Market Expansion Strategies', duration: '90 min', speaker: 'Lisa Rodriguez' }
        ]
      },
      {
        name: 'Financial Management',
        sessions: [
          { title: 'Financial Planning & Forecasting', duration: '90 min', speaker: 'Lisa Rodriguez' },
          { title: 'Cash Flow Optimization', duration: '90 min', speaker: 'Dr. Sarah Johnson' },
          { title: 'Investment Strategies', duration: '90 min', speaker: 'David Thompson' }
        ]
      },
      {
        name: 'Technology & Automation',
        sessions: [
          { title: 'Digital Transformation', duration: '90 min', speaker: 'Michael Chen' },
          { title: 'Automation Implementation', duration: '90 min', speaker: 'Dr. Sarah Johnson' },
          { title: 'Technology ROI', duration: '90 min', speaker: 'Lisa Rodriguez' }
        ]
      }
    ],
    pricingPlans: [
      {
        name: 'Early Bird',
        price: 1295,
        availableUntil: '2024-05-31',
        features: ['Full conference access', 'Networking events', 'Resource materials', 'Follow-up support']
      },
      {
        name: 'Regular',
        price: 1495,
        availableUntil: '2024-07-14',
        features: ['Full conference access', 'Networking events', 'Resource materials', 'Follow-up support']
      },
      {
        name: 'Group Rate',
        price: 1195,
        availableUntil: '2024-07-14',
        features: ['Full conference access', 'Networking events', 'Resource materials', 'Follow-up support', 'Group discounts']
      }
    ],
    includedActivities: [
      {
        title: 'Networking Reception',
        description: 'Join fellow attendees for an evening of networking, refreshments, and building valuable business connections.',
        image: ''
      },
      {
        title: 'Interactive Workshops',
        description: 'Participate in hands-on workshops designed to help you implement strategies learned during the conference.',
        image: ''
      },
      {
        title: 'Expert Q&A Sessions',
        description: 'Get your specific questions answered by industry experts in intimate, focused discussion groups.',
        image: ''
      }
    ]
  });

  const calculateProgress = (current, target) => {
    return target > 0 ? (current / target) * 100 : 0;
  };

  const handleSaveEventDetails = async () => {
    try {
      await saveSummitEventDetails(eventDetails);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving event details:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleTargetEdit = (targetType) => {
    setEditingTarget(targetType);
    if (targetType === 'registrations') {
      setTempTargetValue(summitStats.targetRegistrations?.toString() || '');
    } else if (targetType === 'revenue') {
      setTempTargetValue(summitStats.targetRevenue?.toString() || '');
    }
  };

  const handleTargetSave = async () => {
    try {
      const updatedStats = { ...summitStats };
      if (editingTarget === 'registrations') {
        updatedStats.targetRegistrations = parseInt(tempTargetValue) || 0;
      } else if (editingTarget === 'revenue') {
        updatedStats.targetRevenue = parseInt(tempTargetValue) || 0;
      }
      
      // Here you would typically save to your backend
      // await saveSummitStats(updatedStats);
      setSummitStats(updatedStats);
      setEditingTarget(null);
      setTempTargetValue('');
    } catch (error) {
      console.error('Error saving target:', error);
    }
  };

  const handleTargetCancel = () => {
    setEditingTarget(null);
    setTempTargetValue('');
  };

  const handleSpeakerImageUpload = async (speakerIndex, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP).');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    try {
      setUploadingImages(prev => ({ ...prev, [speakerIndex]: true }));
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `summit-speaker-${speakerIndex}-${timestamp}-${file.name}`;
      const storageRef = ref(storage, `summit/speakers/${fileName}`);
      
      // Upload file
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      // Update the speaker's image URL
      const newSpeakers = [...eventDetails.speakers];
      newSpeakers[speakerIndex] = { ...newSpeakers[speakerIndex], image: downloadURL };
      setEventDetails({ ...eventDetails, speakers: newSpeakers });
      
    } catch (error) {
      console.error('Error uploading speaker image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImages(prev => ({ ...prev, [speakerIndex]: false }));
    }
  };

  const handleRemoveSpeakerImage = (speakerIndex) => {
    const newSpeakers = [...eventDetails.speakers];
    newSpeakers[speakerIndex] = { ...newSpeakers[speakerIndex], image: '' };
    setEventDetails({ ...eventDetails, speakers: newSpeakers });
  };

  const handleHeaderImageUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP).');
      return;
    }

    // Validate file size (max 10MB for header images)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB.');
      return;
    }

    try {
      setUploadingHeaderImage(true);
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `summit-header-${timestamp}-${file.name}`;
      const storageRef = ref(storage, `summit/header/${fileName}`);
      
      // Upload file
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      // Update the header image URL
      setEventDetails({ ...eventDetails, headerImage: downloadURL });
      
    } catch (error) {
      console.error('Error uploading header image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingHeaderImage(false);
    }
  };

  const handleActivityImageUpload = async (activityIndex, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP).');
      return;
    }

    // Validate file size (max 5MB for activity images)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    try {
      setUploadingActivityImages(prev => ({ ...prev, [activityIndex]: true }));
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `summit-activity-${activityIndex}-${timestamp}-${file.name}`;
      const storageRef = ref(storage, `summit/activities/${fileName}`);
      
      // Upload file
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      // Update the activity's image URL
      const newActivities = [...eventDetails.includedActivities];
      newActivities[activityIndex] = { ...newActivities[activityIndex], image: downloadURL };
      setEventDetails({ ...eventDetails, includedActivities: newActivities });
      
    } catch (error) {
      console.error('Error uploading activity image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingActivityImages(prev => ({ ...prev, [activityIndex]: false }));
    }
  };

  const handleRemoveHeaderImage = () => {
    setEventDetails({ ...eventDetails, headerImage: '' });
  };

  const handleRemoveActivityImage = (activityIndex) => {
    const newActivities = [...eventDetails.includedActivities];
    newActivities[activityIndex] = { ...newActivities[activityIndex], image: '' };
    setEventDetails({ ...eventDetails, includedActivities: newActivities });
  };

  const handleRegistrationClick = (registration) => {
    setSelectedRegistration(registration);
    setShowRegistrationModal(true);
  };

  const closeRegistrationModal = () => {
    setShowRegistrationModal(false);
    setSelectedRegistration(null);
  };

  const handleDeleteRegistration = async () => {
    if (!selectedRegistration) return;
    
    if (window.confirm('Are you sure you want to delete this registration? This action cannot be undone.')) {
      try {
        // Here you would typically call an API to delete the registration
        // await deleteSummitRegistration(selectedRegistration.id);
        
        // Remove from local state
        const updatedRegistrations = registrations.filter(reg => reg.id !== selectedRegistration.id);
        setRegistrations(updatedRegistrations);
        
        // Close modal
        closeRegistrationModal();
        
        // You might want to show a success message here
        console.log('Registration deleted successfully');
      } catch (error) {
        console.error('Error deleting registration:', error);
        alert('Failed to delete registration. Please try again.');
      }
    }
  };

  const totalAttendees = registrations.reduce((total, reg) => {
    const guestCount = reg.guests ? reg.guests.length : 0;
    return total + 1 + guestCount;
  }, 0);
  const totalGuests = registrations.reduce((total, reg) => {
    return total + (reg.guests ? reg.guests.length : 0);
  }, 0);

  const stats = !loading && activeTab === 'registrations' ? [
    { icon: Users, value: totalAttendees, label: 'Total Attendees' },
    { icon: UserCheck, value: registrations.length, label: 'Primary Registrants' },
    { icon: UserPlus, value: totalGuests, label: 'Total Guests' },
  ] : [];

  const dashboardToggleProps = {
    onClick: handleToggleDashboardCard,
    disabled: dashboardCardLoading || savingDashboardCard,
    title: showOnDashboard
      ? 'The BBA Summit card is showing on every client dashboard — click to hide it'
      : 'The BBA Summit card is hidden from client dashboards — click to show it',
  };
  const dashboardToggleContent = (
    <>
      {showOnDashboard ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      <span>
        {dashboardCardLoading
          ? 'Loading…'
          : savingDashboardCard
            ? 'Saving…'
            : showOnDashboard
              ? 'On Client Dashboards'
              : 'Off Client Dashboards'}
      </span>
    </>
  );

  return (
    <GlassPage className="text-gray-900 dark:text-white" backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        onBack={() => navigate('/crm/reports')}
        backTitle="Back to reports"
        center={<SlidingPills options={TAB_OPTIONS} value={activeTab} onChange={setActiveTab} ariaLabel="Summit sections" />}
        title={<>Summit<br />Overview</>}
        stats={stats}
        statsKey={activeTab}
        controls={showOnDashboard ? (
          <GlassAccentButton {...dashboardToggleProps}>{dashboardToggleContent}</GlassAccentButton>
        ) : (
          <button type="button" className={GLASS_SECONDARY_BUTTON} {...dashboardToggleProps}>{dashboardToggleContent}</button>
        )}
      />

      {/* Content */}
      {loading ? (
        <div className="flex h-64 items-center justify-center"><BBALoader size={96} /></div>
      ) : (
        <div key={activeTab}>
        {/* Registrations Tab */}
        {(cardMode || activeTab === 'registrations') && (
          <PinnableCard id="registrations" title="All Registrations" size="wide">
          <GlassCard className="p-5" delay={0}>
            <GlassCardHeader
              title="All Registrations"
              right={(
                <GlassSearch
                  placeholder="Search registrants"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48 sm:w-72"
                />
              )}
            />

            {filteredRegistrations.length === 0 ? (
              <GlassEmpty className="h-32">
                {registrations.length === 0 ? 'No registrations found.' : 'No registrations match your search.'}
              </GlassEmpty>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr>
                      <th className={GLASS_TH}>Name</th>
                      <th className={GLASS_TH}>Title</th>
                      <th className={GLASS_TH}>Email</th>
                      <th className={GLASS_TH}>Date</th>
                      <th className={GLASS_TH}>Count</th>
                      <th className={GLASS_TH}>Amount</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-white/75">
                    {filteredRegistrations.map((registration, index) => (
                      <tr
                        key={registration.id || index}
                        className={`${GLASS_ROW} cursor-pointer`}
                        onClick={() => handleRegistrationClick(registration)}
                      >
                        <td className="py-3 pr-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <GlassAvatar name={`${registration.firstName || ''} ${registration.lastName || ''}`} />
                            <span className="font-medium text-gray-900 dark:text-white">
                              {registration.firstName} {registration.lastName}
                            </span>
                          </div>
                        </td>
                        <td className={`py-3 pr-4 whitespace-nowrap ${MUTED}`}>
                          {registration.title || 'N/A'}
                        </td>
                        <td className={`py-3 pr-4 whitespace-nowrap ${MUTED}`}>
                          {registration.email || registration.userEmail}
                        </td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          {registration.registrationDate instanceof Date ? registration.registrationDate.toLocaleDateString() :
                           registration.registrationDate ? new Date(registration.registrationDate).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="py-3 pr-4 whitespace-nowrap">
                          <GlassBadge tone="blue">
                            {registration.guests ? registration.guests.length + 1 : 1}
                          </GlassBadge>
                          {registration.guests && registration.guests.length > 0 && (
                            <div className={`text-xs mt-1 ${MUTED}`}>
                              +{registration.guests.length} guest{registration.guests.length > 1 ? 's' : ''}
                            </div>
                          )}
                        </td>
                        <td className={`py-3 whitespace-nowrap font-medium ${GLASS_TEXT.mint}`}>Free</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </GlassCard>
          </PinnableCard>
        )}

        {/* Event Details Tab */}
        {(cardMode || activeTab === 'event details') && (
          <div className="space-y-4">
            <div className="glass-rise flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-2xl font-medium tracking-tight text-gray-900 dark:text-white">Event Details</h2>
              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <GlassAccentButton onClick={handleSaveEventDetails}>
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </GlassAccentButton>
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className={GLASS_SECONDARY_BUTTON}
                    >
                      <X className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                  </>
                ) : (
                  <GlassAccentButton onClick={() => setIsEditing(true)}>
                    <Edit3 className="w-4 h-4" />
                    <span>Edit Event Details</span>
                  </GlassAccentButton>
                )}
              </div>
            </div>

            {/* Basic Event Information */}
            <PinnableCard id="basic-information" title="Basic Information">
            <GlassCard className="p-5" delay={0}>
              <GlassCardHeader title="Basic Information" className="mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Start Date</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={eventDetails.startDate}
                      onChange={(e) => setEventDetails({...eventDetails, startDate: e.target.value})}
                      className={GLASS_FIELD}
                    />
                  ) : (
                    <p className={VALUE}>{eventDetails.startDate}</p>
                  )}
                </div>
                <div>
                  <label className={LABEL}>End Date</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={eventDetails.endDate}
                      onChange={(e) => setEventDetails({...eventDetails, endDate: e.target.value})}
                      className={GLASS_FIELD}
                    />
                  ) : (
                    <p className={VALUE}>{eventDetails.endDate}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL}>Location</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={eventDetails.location}
                      onChange={(e) => setEventDetails({...eventDetails, location: e.target.value})}
                      className={GLASS_FIELD}
                    />
                  ) : (
                    <p className={VALUE}>{eventDetails.location}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className={LABEL}>Header Image</label>
                  {isEditing ? (
                    <div>
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className={`w-32 h-24 ${DROPZONE}`}>
                            {eventDetails.headerImage ? (
                              <img
                                src={eventDetails.headerImage}
                                alt="Header"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <Upload className={`w-8 h-8 ${ICON_MUTED}`} />
                            )}
                          </div>
                          {uploadingHeaderImage && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap gap-2">
                            <label className={UPLOAD_LABEL}>
                              <Upload className="w-4 h-4" />
                              {eventDetails.headerImage ? 'Change Image' : 'Upload Image'}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleHeaderImageUpload(e.target.files[0])}
                                className="hidden"
                                disabled={uploadingHeaderImage}
                              />
                            </label>
                            {eventDetails.headerImage && (
                              <button
                                type="button"
                                onClick={handleRemoveHeaderImage}
                                className={GLASS_SECONDARY_BUTTON}
                                disabled={uploadingHeaderImage}
                              >
                                <X className="w-4 h-4" />
                                Remove
                              </button>
                            )}
                          </div>
                          <p className={`text-xs mt-2 ${MUTED}`}>
                            JPEG, PNG, GIF, WebP up to 10MB
                          </p>
                        </div>
                      </div>
                      {eventDetails.headerImage && (
                        <div className="mt-4">
                          <img
                            src={eventDetails.headerImage}
                            alt="Header Preview"
                            className={`w-full h-48 object-cover ${IMAGE_FRAME}`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      {eventDetails.headerImage ? (
                        <img
                          src={eventDetails.headerImage}
                          alt="Header"
                          className={`w-full h-48 object-cover ${IMAGE_FRAME}`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className={`w-full h-48 ${DROPZONE}`}>
                          <div className="text-center">
                            <Upload className={`w-12 h-12 mx-auto mb-2 ${ICON_MUTED}`} />
                            <p className={MUTED}>No header image set</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </GlassCard>
            </PinnableCard>

            {/* Overview Section */}
            <PinnableCard id="overview" title="Overview">
            <GlassCard className="p-5" delay={80}>
              <GlassCardHeader title="Overview" className="mb-4" />

              <div className="space-y-4">
                <div>
                  <label className={LABEL}>Why Attend the BBA Summit?</label>
                  {isEditing ? (
                    <textarea
                      value={eventDetails.overview.whyAttend}
                      onChange={(e) => setEventDetails({
                        ...eventDetails,
                        overview: {...eventDetails.overview, whyAttend: e.target.value}
                      })}
                      rows={4}
                      className={GLASS_FIELD}
                    />
                  ) : (
                    <p className={VALUE}>{eventDetails.overview.whyAttend}</p>
                  )}
                </div>

                <div>
                  <label className={LABEL}>What You'll Gain - Knowledge & Skills</label>
                  {isEditing ? (
                    <div className="space-y-2">
                      {eventDetails.overview.knowledgeSkills.map((skill, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={skill}
                            onChange={(e) => {
                              const newSkills = [...eventDetails.overview.knowledgeSkills];
                              newSkills[index] = e.target.value;
                              setEventDetails({
                                ...eventDetails,
                                overview: {...eventDetails.overview, knowledgeSkills: newSkills}
                              });
                            }}
                            className={`flex-1 ${GLASS_FIELD}`}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newSkills = eventDetails.overview.knowledgeSkills.filter((_, i) => i !== index);
                              setEventDetails({
                                ...eventDetails,
                                overview: {...eventDetails.overview, knowledgeSkills: newSkills}
                              });
                            }}
                            className={REMOVE_BUTTON}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newSkills = [...eventDetails.overview.knowledgeSkills, ''];
                          setEventDetails({
                            ...eventDetails,
                            overview: {...eventDetails.overview, knowledgeSkills: newSkills}
                          });
                        }}
                        className={GLASS_SECONDARY_BUTTON}
                      >
                        Add Skill
                      </button>
                    </div>
                  ) : (
                    <ul className={`list-disc list-inside space-y-1 ${VALUE}`}>
                      {eventDetails.overview.knowledgeSkills.map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <label className={LABEL}>Resources & Support</label>
                  {isEditing ? (
                    <div className="space-y-2">
                      {eventDetails.overview.resourcesSupport.map((resource, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={resource}
                            onChange={(e) => {
                              const newResources = [...eventDetails.overview.resourcesSupport];
                              newResources[index] = e.target.value;
                              setEventDetails({
                                ...eventDetails,
                                overview: {...eventDetails.overview, resourcesSupport: newResources}
                              });
                            }}
                            className={`flex-1 ${GLASS_FIELD}`}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newResources = eventDetails.overview.resourcesSupport.filter((_, i) => i !== index);
                              setEventDetails({
                                ...eventDetails,
                                overview: {...eventDetails.overview, resourcesSupport: newResources}
                              });
                            }}
                            className={REMOVE_BUTTON}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newResources = [...eventDetails.overview.resourcesSupport, ''];
                          setEventDetails({
                            ...eventDetails,
                            overview: {...eventDetails.overview, resourcesSupport: newResources}
                          });
                        }}
                        className={GLASS_SECONDARY_BUTTON}
                      >
                        Add Resource
                      </button>
                    </div>
                  ) : (
                    <ul className={`list-disc list-inside space-y-1 ${VALUE}`}>
                      {eventDetails.overview.resourcesSupport.map((resource, index) => (
                        <li key={index}>{resource}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </GlassCard>
            </PinnableCard>

            {/* Conference Schedule */}
            <PinnableCard id="conference-schedule" title="Conference Schedule">
            <GlassCard className="p-5" delay={140}>
              <GlassCardHeader title="Conference Schedule" className="mb-4" />
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.schedule.map((day, dayIndex) => (
                    <div key={dayIndex} className={`${GLASS_INSET} p-4`}>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          value={day.day}
                          onChange={(e) => {
                            const newSchedule = [...eventDetails.schedule];
                            newSchedule[dayIndex] = {...day, day: e.target.value};
                            setEventDetails({...eventDetails, schedule: newSchedule});
                          }}
                          className={`flex-1 ${GLASS_FIELD}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newSchedule = eventDetails.schedule.filter((_, i) => i !== dayIndex);
                            setEventDetails({...eventDetails, schedule: newSchedule});
                          }}
                          className={REMOVE_BUTTON}
                        >
                          Remove Day
                        </button>
                      </div>
                      <div className="space-y-2">
                        {day.events.map((event, eventIndex) => (
                          <div key={eventIndex} className="flex gap-2">
                            <input
                              type="text"
                              value={event}
                              onChange={(e) => {
                                const newSchedule = [...eventDetails.schedule];
                                newSchedule[dayIndex] = {
                                  ...day,
                                  events: day.events.map((evt, i) => i === eventIndex ? e.target.value : evt)
                                };
                                setEventDetails({...eventDetails, schedule: newSchedule});
                              }}
                              className={`flex-1 ${GLASS_FIELD}`}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newSchedule = [...eventDetails.schedule];
                                newSchedule[dayIndex] = {
                                  ...day,
                                  events: day.events.filter((_, i) => i !== eventIndex)
                                };
                                setEventDetails({...eventDetails, schedule: newSchedule});
                              }}
                              className={REMOVE_BUTTON}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const newSchedule = [...eventDetails.schedule];
                            newSchedule[dayIndex] = {
                              ...day,
                              events: [...day.events, '']
                            };
                            setEventDetails({...eventDetails, schedule: newSchedule});
                          }}
                          className={GLASS_SECONDARY_BUTTON}
                        >
                          Add Event
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newSchedule = [...eventDetails.schedule, { day: '', events: [''] }];
                      setEventDetails({...eventDetails, schedule: newSchedule});
                    }}
                    className={GLASS_SECONDARY_BUTTON}
                  >
                    Add Day
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {eventDetails.schedule.map((day, index) => (
                    <div key={index} className={`${GLASS_INSET} p-4`}>
                      <h4 className="font-medium mb-2 text-gray-900 dark:text-white">{day.day}</h4>
                      <ul className={`space-y-1 ${VALUE}`}>
                        {day.events.map((event, eventIndex) => (
                          <li key={eventIndex} className="flex items-center gap-2">
                            <CheckCircle className={`w-4 h-4 flex-shrink-0 ${GLASS_TEXT.mint}`} />
                            {event}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
            </PinnableCard>

            {/* Featured Speakers */}
            <PinnableCard id="featured-speakers" title="Featured Speakers">
            <GlassCard className="p-5" delay={200}>
              <GlassCardHeader title="Featured Speakers" className="mb-4" />
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.speakers.map((speaker, index) => (
                    <div key={index} className={`${GLASS_INSET} p-4`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Name"
                          value={speaker.name}
                          onChange={(e) => {
                            const newSpeakers = [...eventDetails.speakers];
                            newSpeakers[index] = {...speaker, name: e.target.value};
                            setEventDetails({...eventDetails, speakers: newSpeakers});
                          }}
                          className={GLASS_FIELD}
                        />
                        <input
                          type="text"
                          placeholder="Title"
                          value={speaker.title}
                          onChange={(e) => {
                            const newSpeakers = [...eventDetails.speakers];
                            newSpeakers[index] = {...speaker, title: e.target.value};
                            setEventDetails({...eventDetails, speakers: newSpeakers});
                          }}
                          className={GLASS_FIELD}
                        />
                        <input
                          type="text"
                          placeholder="Company"
                          value={speaker.company}
                          onChange={(e) => {
                            const newSpeakers = [...eventDetails.speakers];
                            newSpeakers[index] = {...speaker, company: e.target.value};
                            setEventDetails({...eventDetails, speakers: newSpeakers});
                          }}
                          className={GLASS_FIELD}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newSpeakers = eventDetails.speakers.filter((_, i) => i !== index);
                            setEventDetails({...eventDetails, speakers: newSpeakers});
                          }}
                          className={REMOVE_BUTTON}
                        >
                          Remove Speaker
                        </button>
                      </div>
                      <div className="mt-4">
                        <label className={LABEL}>Speaker Image</label>
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className={`w-20 h-20 ${DROPZONE}`}>
                              {speaker.image ? (
                                <img
                                  src={speaker.image}
                                  alt={speaker.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <Upload className={`w-6 h-6 ${ICON_MUTED}`} />
                              )}
                            </div>
                            {uploadingImages[index] && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap gap-2">
                              <label className={UPLOAD_LABEL}>
                                <Upload className="w-4 h-4" />
                                {speaker.image ? 'Change Image' : 'Upload Image'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleSpeakerImageUpload(index, e.target.files[0])}
                                  className="hidden"
                                  disabled={uploadingImages[index]}
                                />
                              </label>
                              {speaker.image && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSpeakerImage(index)}
                                  className={GLASS_SECONDARY_BUTTON}
                                  disabled={uploadingImages[index]}
                                >
                                  <X className="w-4 h-4" />
                                  Remove
                                </button>
                              )}
                            </div>
                            <p className={`text-xs mt-2 ${MUTED}`}>
                              JPEG, PNG, GIF, WebP up to 5MB
                            </p>
                          </div>
                        </div>
                      </div>
                      <textarea
                        placeholder="Bio"
                        value={speaker.bio}
                        onChange={(e) => {
                          const newSpeakers = [...eventDetails.speakers];
                          newSpeakers[index] = {...speaker, bio: e.target.value};
                          setEventDetails({...eventDetails, speakers: newSpeakers});
                        }}
                        rows={3}
                        className={`mt-4 ${GLASS_FIELD}`}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newSpeakers = [...eventDetails.speakers, { name: '', title: '', company: '', bio: '', image: '' }];
                      setEventDetails({...eventDetails, speakers: newSpeakers});
                    }}
                    className={GLASS_SECONDARY_BUTTON}
                  >
                    Add Speaker
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {eventDetails.speakers.map((speaker, index) => (
                    <div key={index} className={`${GLASS_INSET} p-4`}>
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-2xl overflow-hidden bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                          {speaker.image ? (
                            <img
                              src={speaker.image}
                              alt={speaker.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-8 h-8 bg-black/[0.06] dark:bg-white/[0.08] rounded-full flex items-center justify-center">
                              <Users className={`w-4 h-4 ${ICON_MUTED}`} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-white">{speaker.name}</h4>
                          <p className={`text-sm ${GLASS_TEXT.red}`}>{speaker.title}</p>
                          <p className={`text-sm mb-2 ${VALUE}`}>{speaker.company}</p>
                          <p className={`text-sm ${VALUE}`}>{speaker.bio}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
            </PinnableCard>

            {/* Sponsors */}
            <PinnableCard id="sponsors" title="Sponsors" size="wide">
            <GlassCard className="p-5" delay={260}>
              <GlassCardHeader title="Sponsors" className="mb-4" />
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.sponsors.map((sponsor, index) => (
                    <div key={index} className={`${GLASS_INSET} p-4`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Sponsor Name"
                          value={sponsor.name}
                          onChange={(e) => {
                            const newSponsors = [...eventDetails.sponsors];
                            newSponsors[index] = {...sponsor, name: e.target.value};
                            setEventDetails({...eventDetails, sponsors: newSponsors});
                          }}
                          className={GLASS_FIELD}
                        />
                        <select
                          value={sponsor.tier}
                          onChange={(e) => {
                            const newSponsors = [...eventDetails.sponsors];
                            newSponsors[index] = {...sponsor, tier: e.target.value};
                            setEventDetails({...eventDetails, sponsors: newSponsors});
                          }}
                          className={`${GLASS_FIELD} dark:[&>option]:bg-[#14110f]`}
                        >
                          <option value="Platinum">Platinum</option>
                          <option value="Gold">Gold</option>
                          <option value="Silver">Silver</option>
                          <option value="Bronze">Bronze</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Logo URL"
                          value={sponsor.logo}
                          onChange={(e) => {
                            const newSponsors = [...eventDetails.sponsors];
                            newSponsors[index] = {...sponsor, logo: e.target.value};
                            setEventDetails({...eventDetails, sponsors: newSponsors});
                          }}
                          className={GLASS_FIELD}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newSponsors = eventDetails.sponsors.filter((_, i) => i !== index);
                            setEventDetails({...eventDetails, sponsors: newSponsors});
                          }}
                          className={REMOVE_BUTTON}
                        >
                          Remove Sponsor
                        </button>
                      </div>
                      <textarea
                        placeholder="Description"
                        value={sponsor.description}
                        onChange={(e) => {
                          const newSponsors = [...eventDetails.sponsors];
                          newSponsors[index] = {...sponsor, description: e.target.value};
                          setEventDetails({...eventDetails, sponsors: newSponsors});
                        }}
                        rows={3}
                        className={`mt-4 ${GLASS_FIELD}`}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newSponsors = [...eventDetails.sponsors, { name: '', tier: 'Bronze', logo: '', description: '' }];
                      setEventDetails({...eventDetails, sponsors: newSponsors});
                    }}
                    className={GLASS_SECONDARY_BUTTON}
                  >
                    Add Sponsor
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {eventDetails.sponsors.map((sponsor, index) => (
                    <div key={index} className={`${GLASS_INSET} p-4 text-center`}>
                      <div className="mb-2">
                        <GlassBadge tone={SPONSOR_TIER_TONE[sponsor.tier] || 'coral'}>
                          {sponsor.tier}
                        </GlassBadge>
                      </div>
                      <h4 className="font-medium mb-2 text-gray-900 dark:text-white">{sponsor.name}</h4>
                      <p className={`text-sm ${VALUE}`}>{sponsor.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
            </PinnableCard>

            {/* Learning Tracks */}
            <PinnableCard id="learning-tracks" title="Learning Tracks">
            <GlassCard className="p-5" delay={320}>
              <GlassCardHeader title="Learning Tracks" className="mb-4" />
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.learningTracks.map((track, trackIndex) => (
                    <div key={trackIndex} className={`${GLASS_INSET} p-4`}>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          placeholder="Track Name"
                          value={track.name}
                          onChange={(e) => {
                            const newTracks = [...eventDetails.learningTracks];
                            newTracks[trackIndex] = {...track, name: e.target.value};
                            setEventDetails({...eventDetails, learningTracks: newTracks});
                          }}
                          className={`flex-1 ${GLASS_FIELD}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newTracks = eventDetails.learningTracks.filter((_, i) => i !== trackIndex);
                            setEventDetails({...eventDetails, learningTracks: newTracks});
                          }}
                          className={REMOVE_BUTTON}
                        >
                          Remove Track
                        </button>
                      </div>
                      <div className="space-y-2">
                        {track.sessions.map((session, sessionIndex) => (
                          <div key={sessionIndex} className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Session Name"
                              value={session}
                              onChange={(e) => {
                                const newTracks = [...eventDetails.learningTracks];
                                newTracks[trackIndex] = {
                                  ...track,
                                  sessions: track.sessions.map((s, i) => i === sessionIndex ? e.target.value : s)
                                };
                                setEventDetails({...eventDetails, learningTracks: newTracks});
                              }}
                              className={`flex-1 ${GLASS_FIELD}`}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newTracks = [...eventDetails.learningTracks];
                                newTracks[trackIndex] = {
                                  ...track,
                                  sessions: track.sessions.filter((_, i) => i !== sessionIndex)
                                };
                                setEventDetails({...eventDetails, learningTracks: newTracks});
                              }}
                              className={REMOVE_BUTTON}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const newTracks = [...eventDetails.learningTracks];
                            newTracks[trackIndex] = {
                              ...track,
                              sessions: [...track.sessions, '']
                            };
                            setEventDetails({...eventDetails, learningTracks: newTracks});
                          }}
                          className={GLASS_SECONDARY_BUTTON}
                        >
                          Add Session
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newTracks = [...eventDetails.learningTracks, { name: '', sessions: [''] }];
                      setEventDetails({...eventDetails, learningTracks: newTracks});
                    }}
                    className={GLASS_SECONDARY_BUTTON}
                  >
                    Add Track
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {eventDetails.learningTracks.map((track, index) => (
                    <div key={index} className={`${GLASS_INSET} p-4`}>
                      <h4 className="font-medium mb-3 text-gray-900 dark:text-white">{track.name}</h4>
                      <ul className="space-y-2">
                        {track.sessions.map((session, sessionIndex) => (
                          <li key={sessionIndex} className={`flex items-center gap-2 ${VALUE}`}>
                            <CheckCircle className={`w-4 h-4 flex-shrink-0 ${GLASS_TEXT.mint}`} />
                            {session}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
            </PinnableCard>

            {/* Registration Pricing Plans */}
            <PinnableCard id="pricing-plans" title="Registration Pricing Plans" size="wide">
            <GlassCard className="p-5" delay={380}>
              <GlassCardHeader title="Registration Pricing Plans" className="mb-4" />
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.pricingPlans.map((plan, index) => (
                    <div key={index} className={`${GLASS_INSET} p-4`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Plan Name"
                          value={plan.name}
                          onChange={(e) => {
                            const newPlans = [...eventDetails.pricingPlans];
                            newPlans[index] = {...plan, name: e.target.value};
                            setEventDetails({...eventDetails, pricingPlans: newPlans});
                          }}
                          className={GLASS_FIELD}
                        />
                        <input
                          type="number"
                          placeholder="Price"
                          value={plan.price}
                          onChange={(e) => {
                            const newPlans = [...eventDetails.pricingPlans];
                            newPlans[index] = {...plan, price: parseInt(e.target.value) || 0};
                            setEventDetails({...eventDetails, pricingPlans: newPlans});
                          }}
                          className={GLASS_FIELD}
                        />
                        <input
                          type="date"
                          placeholder="Available Until"
                          value={plan.availableUntil}
                          onChange={(e) => {
                            const newPlans = [...eventDetails.pricingPlans];
                            newPlans[index] = {...plan, availableUntil: e.target.value};
                            setEventDetails({...eventDetails, pricingPlans: newPlans});
                          }}
                          className={GLASS_FIELD}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newPlans = eventDetails.pricingPlans.filter((_, i) => i !== index);
                            setEventDetails({...eventDetails, pricingPlans: newPlans});
                          }}
                          className={REMOVE_BUTTON}
                        >
                          Remove Plan
                        </button>
                      </div>
                      <div className="mt-4">
                        <label className={LABEL}>Features</label>
                        <div className="space-y-2">
                          {plan.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex gap-2">
                              <input
                                type="text"
                                placeholder="Feature"
                                value={feature}
                                onChange={(e) => {
                                  const newPlans = [...eventDetails.pricingPlans];
                                  newPlans[index] = {
                                    ...plan,
                                    features: plan.features.map((f, i) => i === featureIndex ? e.target.value : f)
                                  };
                                  setEventDetails({...eventDetails, pricingPlans: newPlans});
                                }}
                                className={`flex-1 ${GLASS_FIELD}`}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newPlans = [...eventDetails.pricingPlans];
                                  newPlans[index] = {
                                    ...plan,
                                    features: plan.features.filter((_, i) => i !== featureIndex)
                                  };
                                  setEventDetails({...eventDetails, pricingPlans: newPlans});
                                }}
                                className={REMOVE_BUTTON}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const newPlans = [...eventDetails.pricingPlans];
                              newPlans[index] = {
                                ...plan,
                                features: [...plan.features, '']
                              };
                              setEventDetails({...eventDetails, pricingPlans: newPlans});
                            }}
                            className={GLASS_SECONDARY_BUTTON}
                          >
                            Add Feature
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newPlans = [...eventDetails.pricingPlans, {
                        name: '',
                        price: 0,
                        availableUntil: '',
                        features: ['']
                      }];
                      setEventDetails({...eventDetails, pricingPlans: newPlans});
                    }}
                    className={GLASS_SECONDARY_BUTTON}
                  >
                    Add Plan
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {eventDetails.pricingPlans.map((plan, index) => (
                    <div key={index} className={`${GLASS_INSET} p-5 text-center`}>
                      <h4 className="font-medium text-lg mb-2 text-gray-900 dark:text-white">{plan.name}</h4>
                      <GlassFigure className="mb-4 justify-center" value={formatCurrency(plan.price)} />
                      <p className={`text-sm mb-4 ${MUTED}`}>Available until {plan.availableUntil}</p>
                      <ul className="space-y-2 text-left">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className={`flex items-center gap-2 text-sm ${VALUE}`}>
                            <CheckCircle className={`w-4 h-4 flex-shrink-0 ${GLASS_TEXT.mint}`} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
            </PinnableCard>

            {/* Included Activities */}
            <PinnableCard id="included-activities" title="Included Activities" size="wide">
            <GlassCard className="p-5" delay={440}>
              <GlassCardHeader title="Included Activities" className="mb-4" />
              {isEditing ? (
                <div className="space-y-4">
                  {eventDetails.includedActivities?.map((activity, index) => (
                    <div key={index} className={`${GLASS_INSET} p-4`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Activity Title"
                          value={activity.title}
                          onChange={(e) => {
                            const newActivities = [...eventDetails.includedActivities];
                            newActivities[index] = {...activity, title: e.target.value};
                            setEventDetails({...eventDetails, includedActivities: newActivities});
                          }}
                          className={GLASS_FIELD}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newActivities = eventDetails.includedActivities.filter((_, i) => i !== index);
                            setEventDetails({...eventDetails, includedActivities: newActivities});
                          }}
                          className={REMOVE_BUTTON}
                        >
                          Remove Activity
                        </button>
                      </div>
                      <div className="mt-4">
                        <label className={LABEL}>Activity Image</label>
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className={`w-24 h-20 ${DROPZONE}`}>
                              {activity.image ? (
                                <img
                                  src={activity.image}
                                  alt={activity.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <Upload className={`w-6 h-6 ${ICON_MUTED}`} />
                              )}
                            </div>
                            {uploadingActivityImages[index] && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-2xl">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex flex-wrap gap-2">
                              <label className={UPLOAD_LABEL}>
                                <Upload className="w-4 h-4" />
                                {activity.image ? 'Change' : 'Upload'}
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleActivityImageUpload(index, e.target.files[0])}
                                  className="hidden"
                                  disabled={uploadingActivityImages[index]}
                                />
                              </label>
                              {activity.image && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveActivityImage(index)}
                                  className={GLASS_SECONDARY_BUTTON}
                                  disabled={uploadingActivityImages[index]}
                                >
                                  <X className="w-4 h-4" />
                                  Remove
                                </button>
                              )}
                            </div>
                            <p className={`text-xs mt-2 ${MUTED}`}>
                              JPEG, PNG, GIF, WebP up to 5MB
                            </p>
                          </div>
                        </div>
                        {activity.image && (
                          <div className="mt-3">
                            <img
                              src={activity.image}
                              alt={`${activity.title} Preview`}
                              className={`w-32 h-24 object-cover ${IMAGE_FRAME}`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <textarea
                        placeholder="Activity Description"
                        value={activity.description}
                        onChange={(e) => {
                          const newActivities = [...eventDetails.includedActivities];
                          newActivities[index] = {...activity, description: e.target.value};
                          setEventDetails({...eventDetails, includedActivities: newActivities});
                        }}
                        rows={3}
                        className={`mt-4 ${GLASS_FIELD}`}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newActivities = [...(eventDetails.includedActivities || []), { title: '', description: '', image: '' }];
                      setEventDetails({...eventDetails, includedActivities: newActivities});
                    }}
                    className={GLASS_SECONDARY_BUTTON}
                  >
                    Add Activity
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {eventDetails.includedActivities?.map((activity, index) => (
                    <div key={index} className={`${GLASS_INSET} overflow-hidden`}>
                      <div className="w-full h-48 bg-black/[0.03] dark:bg-white/[0.04] flex items-center justify-center">
                        {activity.image ? (
                          <img
                            src={activity.image}
                            alt={activity.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="text-center">
                            <Upload className={`w-12 h-12 mx-auto mb-2 ${ICON_MUTED}`} />
                            <p className={`text-sm ${MUTED}`}>No image</p>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h4 className="font-medium mb-2 text-gray-900 dark:text-white">{activity.title}</h4>
                        <p className={`text-sm ${VALUE}`}>{activity.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
            </PinnableCard>
          </div>
        )}
        </div>
      )}

      {/* Registration Details Modal — kept outside the glass cards (backdrop-filter traps fixed overlays) */}
      {showRegistrationModal && selectedRegistration && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={REGISTRATION_MODAL_PANEL}>
            {/* Modal Header */}
            <div className="flex justify-center items-center mb-6 relative min-h-[2.5rem]">
              <h3 className="text-xl font-medium">Registration Details</h3>
              <div className="absolute right-0">
                <GlassCircleButton onClick={closeRegistrationModal} title="Close">
                  <X className="w-4 h-4" />
                </GlassCircleButton>
              </div>
            </div>

            {/* Registration Information */}
            <div className="space-y-4">
              {/* Primary Registrant */}
              <div className={`${GLASS_INSET} p-4`}>
                <h4 className={`text-base font-semibold mb-4 ${GLASS_TEXT.red}`}>Primary Registrant</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={MODAL_LABEL}>Name</label>
                    <p className="text-gray-900 dark:text-white">
                      {selectedRegistration.firstName} {selectedRegistration.lastName}
                    </p>
                  </div>
                  <div>
                    <label className={MODAL_LABEL}>Title</label>
                    <p className="text-gray-900 dark:text-white">
                      {selectedRegistration.title || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className={MODAL_LABEL}>Email</label>
                    <p className="text-gray-900 dark:text-white">
                      {selectedRegistration.email || selectedRegistration.userEmail || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className={MODAL_LABEL}>Registration Date</label>
                    <p className="text-gray-900 dark:text-white">
                      {selectedRegistration.registrationDate instanceof Date ?
                        selectedRegistration.registrationDate.toLocaleDateString() :
                        selectedRegistration.registrationDate ?
                          new Date(selectedRegistration.registrationDate).toLocaleDateString() :
                          'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Guests */}
              {selectedRegistration.guests && selectedRegistration.guests.length > 0 && (
                <div className={`${GLASS_INSET} p-4`}>
                  <h4 className={`text-base font-semibold mb-4 text-center ${GLASS_TEXT.blue}`}>
                    Guests ({selectedRegistration.guests.length})
                  </h4>
                  <div className="space-y-3">
                    {selectedRegistration.guests.map((guest, index) => (
                      <div key={index} className="p-4 rounded-2xl border border-black/[0.05] bg-white/60 dark:border-white/[0.06] dark:bg-white/[0.04]">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-[#5BC0EB]/20 dark:bg-[#5BC0EB]/[0.15] rounded-full flex items-center justify-center flex-shrink-0">
                            <Users className={`w-4 h-4 ${GLASS_TEXT.blue}`} />
                          </div>
                          <div className="flex-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className={`block text-xs font-medium mb-1 ${MUTED}`}>Name</label>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {guest.firstName} {guest.lastName}
                                </p>
                              </div>
                              <div>
                                <label className={`block text-xs font-medium mb-1 ${MUTED}`}>Email</label>
                                <p className={`text-sm ${VALUE}`}>
                                  {guest.email || 'N/A'}
                                </p>
                              </div>
                              <div className="md:col-span-2">
                                <label className={`block text-xs font-medium mb-1 ${MUTED}`}>Title</label>
                                <p className={`text-sm ${VALUE}`}>
                                  {guest.title || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Information */}
              <div className={`${GLASS_INSET} p-4`}>
                <h4 className={`text-base font-semibold mb-4 text-center ${GLASS_TEXT.mint}`}>Registration Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className={`text-2xl font-semibold tabular-nums ${GLASS_TEXT.red}`}>
                      {selectedRegistration.guests ? selectedRegistration.guests.length + 1 : 1}
                    </div>
                    <div className={`text-sm ${MUTED}`}>Total Attendees</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-semibold ${GLASS_TEXT.mint}`}>Free</div>
                    <div className={`text-sm ${MUTED}`}>Registration Cost</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-2xl font-semibold tabular-nums ${GLASS_TEXT.blue}`}>
                      {selectedRegistration.guests ? selectedRegistration.guests.length : 0}
                    </div>
                    <div className={`text-sm ${MUTED}`}>Guests</div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              {(selectedRegistration.phone || selectedRegistration.address) && (
                <div className={`${GLASS_INSET} p-4`}>
                  <h4 className="text-base font-semibold mb-4 text-[#6B4FC7] dark:text-[#B9A3F5]">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedRegistration.phone && (
                      <div>
                        <label className={MODAL_LABEL}>Phone</label>
                        <p className="text-gray-900 dark:text-white">{selectedRegistration.phone}</p>
                      </div>
                    )}
                    {selectedRegistration.address && (
                      <div>
                        <label className={MODAL_LABEL}>Address</label>
                        <p className="text-gray-900 dark:text-white">{selectedRegistration.address}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t border-black/[0.06] dark:border-white/[0.08]">
              <GlassAccentButton onClick={handleDeleteRegistration}>
                <Trash2 className="w-4 h-4" />
                Delete Registration
              </GlassAccentButton>
              <button
                type="button"
                onClick={closeRegistrationModal}
                className={GLASS_SECONDARY_BUTTON}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </GlassPage>
  );
};

export default SummitOverview;
